import React, { useEffect, useState } from 'react'
import {
    Alert, Button, Card, Checkbox, Col, Descriptions, Divider, Drawer, Form, Input,
    InputNumber, message, Popconfirm, Row, Select, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import {
    createAppBuildProject, detectAppBuildProject, getAppBuildProjectConfigs, updateAppBuildProject,
} from '../../request'

const { Option } = Select
const { Text, Paragraph } = Typography
const { TextArea } = Input

const TARGETS = [
    { key: 'mac-arm64', name: 'macOS Apple Silicon', platform: 'macos', architecture: 'arm64', executor: 'macos-native' },
    { key: 'mac-x64', name: 'macOS Intel', platform: 'macos', architecture: 'x64', executor: 'macos-native' },
    { key: 'windows-x64', name: 'Windows x64', platform: 'windows', architecture: 'x64', executor: 'windows-cross' },
    { key: 'linux-x64', name: 'Linux x64', platform: 'linux', architecture: 'x64', executor: 'linux-docker' },
    { key: 'linux-arm64', name: 'Linux ARM64', platform: 'linux', architecture: 'arm64', executor: 'linux-docker' },
]

const EMPTY_PROJECT = {
    enabled: true,
    sortNo: 0,
    defaultBranch: 'main',
    templateKey: 'electron-desktop',
    config: {
        applicationDirectory: '.', installDirectory: '.', buildDirectory: '.', packageManager: 'npm',
        installMode: 'frozen', tauriScript: 'tauri', artifactGlobsText: 'dist/**/*', allowedEnvironmentKeysText: '',
    },
    profiles: [{ profileKey: 'test', profileName: '测试环境', environmentJson: '{}', enabled: true, sortNo: 10 }],
    targetKeys: ['mac-arm64'],
    executors: { 'mac-arm64': 'macos-native' },
}

const splitLines = value => String(value || '').split(/[\n,]/).map(item => item.trim()).filter(Boolean)

const executorForTarget = (target, selectedTemplate) => {
    if (target.platform !== 'windows') return target.executor
    return selectedTemplate === 'tauri-desktop' ? 'windows-vm' : 'windows-cross'
}

const parseEnvironment = (value, label) => {
    try {
        const parsed = JSON.parse(value || '{}')
        if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error()
        return parsed
    } catch (e) {
        throw new Error(`${label}的环境变量必须是 JSON 对象`)
    }
}

const toFormValue = project => {
    if (!project) return JSON.parse(JSON.stringify(EMPTY_PROJECT))
    const scripts = (project.config && project.config.profileBuildScripts) || {}
    const executors = {}
    ;(project.targets || []).forEach(item => {
        const target = TARGETS.find(candidate => candidate.key === item.targetKey)
        if (target) executors[item.targetKey] = executorForTarget(target, project.templateKey)
    })
    return {
        ...project,
        config: {
            ...(project.config || {}),
            artifactGlobsText: ((project.config && project.config.artifactGlobs) || []).join('\n'),
            allowedEnvironmentKeysText: ((project.config && project.config.allowedEnvironmentKeys) || []).join('\n'),
        },
        profiles: (project.profiles || []).map(item => ({
            ...item,
            environmentJson: JSON.stringify(item.environment || {}, null, 2),
            buildScript: scripts[item.profileKey],
        })),
        targetKeys: (project.targets || []).map(item => item.targetKey),
        executors,
    }
}

const toPayload = (values, detectionSnapshot) => {
    const profiles = (values.profiles || []).map((item, index) => ({
        profileKey: item.profileKey && item.profileKey.trim(),
        profileName: item.profileName && item.profileName.trim(),
        environment: parseEnvironment(item.environmentJson, item.profileName || `第 ${index + 1} 个环境`),
        enabled: item.enabled !== false,
        sortNo: item.sortNo == null ? (index + 1) * 10 : item.sortNo,
    }))
    const profileBuildScripts = {}
    ;(values.profiles || []).forEach(item => {
        if (item.profileKey && item.buildScript && item.buildScript.trim()) profileBuildScripts[item.profileKey.trim()] = item.buildScript.trim()
    })
    const targets = (values.targetKeys || []).map((key, index) => {
        const target = TARGETS.find(item => item.key === key)
        return {
            targetKey: key,
            targetName: target.name,
            platform: target.platform,
            architecture: target.architecture,
            executorKey: executorForTarget(target, values.templateKey),
            enabled: true,
            sortNo: (index + 1) * 10,
        }
    })
    return {
        projectKey: values.projectKey.trim(),
        projectName: values.projectName.trim(),
        repositoryOwner: values.repositoryOwner.trim(),
        repositoryName: values.repositoryName.trim(),
        defaultBranch: values.defaultBranch.trim(),
        templateKey: values.templateKey,
        detectionSnapshot: detectionSnapshot || values.detectionSnapshot || {},
        enabled: values.enabled !== false,
        sortNo: values.sortNo || 0,
        config: {
            ...values.config,
            applicationDirectory: values.config.applicationDirectory.trim(),
            installDirectory: values.config.installDirectory.trim(),
            buildDirectory: values.config.buildDirectory.trim(),
            buildScript: values.config.buildScript ? values.config.buildScript.trim() : undefined,
            tauriScript: values.config.tauriScript ? values.config.tauriScript.trim() : undefined,
            workspaceFilter: values.config.workspaceFilter ? values.config.workspaceFilter.trim() : undefined,
            profileBuildScripts,
            artifactGlobs: splitLines(values.config.artifactGlobsText),
            allowedEnvironmentKeys: splitLines(values.config.allowedEnvironmentKeysText),
        },
        profiles,
        targets,
    }
}

export default function AppBuildProjectConfig({ onProjectsChanged }) {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [projects, setProjects] = useState([])
    const [visible, setVisible] = useState(false)
    const [editingKey, setEditingKey] = useState(null)
    const [saving, setSaving] = useState(false)
    const [detecting, setDetecting] = useState(false)
    const [detection, setDetection] = useState(null)
    const [selectedTargets, setSelectedTargets] = useState([])
    const [templateKey, setTemplateKey] = useState('electron-desktop')

    const load = () => {
        setLoading(true)
        return getAppBuildProjectConfigs().then(data => setProjects(data || []))
            .catch(() => {})
            .finally(() => setLoading(false))
    }

    useEffect(() => { load() }, [])

    const open = project => {
        const initial = toFormValue(project)
        form.resetFields()
        setEditingKey(project ? project.projectKey : null)
        setDetection(project ? project.detectionSnapshot : null)
        setSelectedTargets(initial.targetKeys || [])
        setTemplateKey(initial.templateKey)
        setVisible(true)
        setTimeout(() => form.setFieldsValue(initial), 0)
    }

    const detect = () => form.validateFields(['repositoryOwner', 'repositoryName']).then(values => {
        setDetecting(true)
        return detectAppBuildProject({
            repositoryOwner: values.repositoryOwner.trim(),
            repositoryName: values.repositoryName.trim(),
            sourceRef: form.getFieldValue('defaultBranch') || undefined,
        }).then(result => {
            setDetection(result)
            const currentDirectory = form.getFieldValue(['config', 'applicationDirectory'])
            const recommendedDirectory = (result.applicationDirectories || [])[0]
            const currentTargets = form.getFieldValue('targetKeys') || []
            const currentExecutors = form.getFieldValue('executors') || {}
            const windowsTarget = TARGETS.find(item => item.key === 'windows-x64')
            form.setFieldsValue({
                defaultBranch: result.defaultBranch,
                templateKey: result.recommendedTemplate,
                executors: currentTargets.includes('windows-x64') ? {
                    ...currentExecutors,
                    'windows-x64': executorForTarget(windowsTarget, result.recommendedTemplate),
                } : currentExecutors,
                config: {
                    packageManager: result.packageManager,
                    applicationDirectory: currentDirectory === '.' && recommendedDirectory ? recommendedDirectory : currentDirectory,
                },
            })
            setTemplateKey(result.recommendedTemplate)
            message.success('检测完成，请继续确认模板、目录、脚本和产物路径')
        }).finally(() => setDetecting(false))
    })

    const save = () => form.validateFields().then(values => {
        let payload
        try { payload = toPayload(values, detection) } catch (error) {
            message.error(error.message)
            return Promise.reject(error)
        }
        setSaving(true)
        const request = editingKey ? updateAppBuildProject(editingKey, payload) : createAppBuildProject(payload)
        return request.then(() => {
            message.success(editingKey ? '项目配置已更新' : '项目已创建')
            setVisible(false)
            load()
            if (onProjectsChanged) onProjectsChanged()
        }).finally(() => setSaving(false))
    }).catch(() => {})

    const columns = [
        { title: '项目', dataIndex: 'projectName', render: (value, row) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{row.projectKey}</Text></Space> },
        { title: 'GitHub 仓库', dataIndex: 'repository', render: value => <Text copyable>{value}</Text> },
        { title: '模板', dataIndex: 'templateKey', render: value => <Tag color={value === 'electron-desktop' ? 'blue' : 'purple'}>{value === 'electron-desktop' ? 'Electron' : 'Tauri'}</Tag> },
        { title: '环境', dataIndex: 'profiles', render: value => (value || []).map(item => <Tag key={item.profileKey}>{item.profileName}</Tag>) },
        { title: '平台', dataIndex: 'targets', render: value => (value || []).map(item => <Tag key={item.targetKey}>{item.targetName}</Tag>) },
        { title: '状态', dataIndex: 'enabled', width: 80, render: value => <Tag color={value ? 'success' : 'default'}>{value ? '启用' : '停用'}</Tag> },
        { title: '操作', key: 'action', width: 80, render: (_, row) => <Button type="link" icon={<EditOutlined />} onClick={() => open(row)}>编辑</Button> },
    ]

    return <>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col><Alert type="info" showIcon message="项目是动态配置" description="新增项目不需要修改 Agent；检测结果只给出模板建议，最终配置由管理员确认。" /></Col>
            <Col><Space><Button icon={<ReloadOutlined />} onClick={load}>刷新</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>新增项目</Button></Space></Col>
        </Row>
        <Table rowKey="projectKey" loading={loading} dataSource={projects} columns={columns} pagination={false} scroll={{ x: 1000 }} />
        <Drawer
            title={editingKey ? `编辑项目 · ${editingKey}` : '新增构建项目'}
            visible={visible}
            width="82%"
            destroyOnClose
            onClose={() => setVisible(false)}
            footer={<div className="app-builder-drawer-footer"><Button onClick={() => setVisible(false)}>取消</Button><Button type="primary" loading={saving} onClick={save}>保存配置</Button></div>}
        >
            <Form form={form} layout="vertical" initialValues={EMPTY_PROJECT} onValuesChange={(changed, all) => {
                if (Object.prototype.hasOwnProperty.call(changed, 'targetKeys')) {
                    const targetKeys = all.targetKeys || []
                    setSelectedTargets(targetKeys)
                    if (targetKeys.includes('windows-x64')) {
                        const windowsTarget = TARGETS.find(item => item.key === 'windows-x64')
                        form.setFieldsValue({
                            executors: {
                                ...(all.executors || {}),
                                'windows-x64': executorForTarget(windowsTarget, all.templateKey),
                            },
                        })
                    }
                }
                if (Object.prototype.hasOwnProperty.call(changed, 'templateKey')) {
                    setTemplateKey(all.templateKey)
                    if ((all.targetKeys || []).includes('windows-x64')) {
                        const windowsTarget = TARGETS.find(item => item.key === 'windows-x64')
                        form.setFieldsValue({
                            executors: {
                                ...(all.executors || {}),
                                'windows-x64': executorForTarget(windowsTarget, all.templateKey),
                            },
                        })
                    }
                }
            }}>
                <Divider orientation="left">基本信息与检测</Divider>
                <Row gutter={16}>
                    <Col span={6}><Form.Item name="projectKey" label="项目标识" rules={[{ required: true }, { pattern: /^[a-z0-9][a-z0-9._-]{0,63}$/, message: '仅允许小写字母、数字、点、横线和下划线' }]}><Input disabled={!!editingKey} placeholder="例如 my-desktop" /></Form.Item></Col>
                    <Col span={6}><Form.Item name="projectName" label="项目名称" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col span={5}><Form.Item name="repositoryOwner" label="GitHub Owner" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col span={5}><Form.Item name="repositoryName" label="GitHub 仓库" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col span={2}><Form.Item label=" "><Button block icon={<SearchOutlined />} loading={detecting} onClick={detect}>检测</Button></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}><Form.Item name="defaultBranch" label="默认分支" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="templateKey" label="构建模板（管理员确认）" rules={[{ required: true }]}><Select><Option value="electron-desktop">Electron</Option><Option value="tauri-desktop">Tauri</Option></Select></Form.Item></Col>
                    <Col span={4}><Form.Item name="sortNo" label="排序"><InputNumber min={0} max={100000} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={4}><Form.Item name="enabled" label="启用" valuePropName="checked"><Switch /></Form.Item></Col>
                </Row>
                {detection ? <Card size="small" className="app-builder-detection">
                    <Descriptions size="small" column={2}>
                        <Descriptions.Item label="固定 Commit"><Text copyable>{detection.commitSha}</Text></Descriptions.Item>
                        <Descriptions.Item label="推荐模板">{detection.recommendedTemplate}</Descriptions.Item>
                        <Descriptions.Item label="包管理器">{detection.packageManager}</Descriptions.Item>
                        <Descriptions.Item label="候选目录">{(detection.applicationDirectories || []).join('、') || '-'}</Descriptions.Item>
                    </Descriptions>
                    <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>{detection.warning}</Paragraph>
                </Card> : null}

                <Divider orientation="left">模板参数</Divider>
                <Row gutter={16}>
                    <Col span={8}><Form.Item name={['config', 'applicationDirectory']} label="应用目录" rules={[{ required: true }]}><Input placeholder="." /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['config', 'installDirectory']} label="依赖安装目录" rules={[{ required: true }]}><Input placeholder="." /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['config', 'buildDirectory']} label="构建目录" rules={[{ required: true }]}><Input placeholder="." /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['config', 'packageManager']} label="包管理器" rules={[{ required: true }]}><Select><Option value="npm">npm</Option><Option value="pnpm">pnpm</Option><Option value="yarn">yarn</Option></Select></Form.Item></Col>
                    <Col span={8}><Form.Item name={['config', 'installMode']} label="依赖安装模式" rules={[{ required: true }]}><Select><Option value="frozen">锁文件模式</Option><Option value="standard">普通安装</Option></Select></Form.Item></Col>
                    <Col span={8}><Form.Item name={['config', 'workspaceFilter']} label="Workspace Filter（可选）"><Input placeholder="例如 @scope/desktop" /></Form.Item></Col>
                    {templateKey === 'electron-desktop' ? <Col span={12}><Form.Item name={['config', 'buildScript']} label="Electron 通用构建脚本（可改为在环境中分别填写）"><Input placeholder="package:desktop" /></Form.Item></Col> : <Col span={12}><Form.Item name={['config', 'tauriScript']} label="Tauri package script" rules={[{ required: true }]}><Input placeholder="tauri" /></Form.Item></Col>}
                    <Col span={12}><Form.Item name={['config', 'artifactGlobsText']} label="产物路径（每行一个）" rules={[{ required: true }]}><TextArea rows={3} placeholder="dist/**/*" /></Form.Item></Col>
                    <Col span={12}><Form.Item name={['config', 'allowedEnvironmentKeysText']} label="允许下发的环境变量（每行一个）"><TextArea rows={3} placeholder={'API_BASE_URL\nAPP_CHANNEL'} /></Form.Item></Col>
                </Row>

                <Divider orientation="left">环境（动态配置）</Divider>
                <Form.List name="profiles">
                    {(fields, { add, remove }) => <>
                        {fields.map((field, index) => <Card size="small" key={field.key} title={`环境 ${index + 1}`} extra={fields.length > 1 ? <Popconfirm title="确认删除该环境？" onConfirm={() => remove(field.name)}><Button type="link" danger>删除</Button></Popconfirm> : null} style={{ marginBottom: 12 }}>
                            <Row gutter={16}>
                                <Col span={5}><Form.Item {...field} name={[field.name, 'profileKey']} fieldKey={[field.fieldKey, 'profileKey']} label="环境标识" rules={[{ required: true }]}><Input placeholder="test / preview / production" /></Form.Item></Col>
                                <Col span={5}><Form.Item {...field} name={[field.name, 'profileName']} fieldKey={[field.fieldKey, 'profileName']} label="环境名称" rules={[{ required: true }]}><Input /></Form.Item></Col>
                                <Col span={5}><Form.Item {...field} name={[field.name, 'buildScript']} fieldKey={[field.fieldKey, 'buildScript']} label="该环境构建脚本"><Input placeholder="可覆盖通用脚本" /></Form.Item></Col>
                                <Col span={3}><Form.Item {...field} name={[field.name, 'sortNo']} fieldKey={[field.fieldKey, 'sortNo']} label="排序"><InputNumber min={0} /></Form.Item></Col>
                                <Col span={2}><Form.Item {...field} name={[field.name, 'enabled']} fieldKey={[field.fieldKey, 'enabled']} label="启用" valuePropName="checked"><Switch /></Form.Item></Col>
                                <Col span={24}><Form.Item {...field} name={[field.name, 'environmentJson']} fieldKey={[field.fieldKey, 'environmentJson']} label="环境变量 JSON" rules={[{ required: true }]}><TextArea rows={3} placeholder='{"API_BASE_URL":"https://test.example.com"}' /></Form.Item></Col>
                            </Row>
                        </Card>)}
                        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ enabled: true, environmentJson: '{}', sortNo: (fields.length + 1) * 10 })}>增加环境</Button>
                    </>}
                </Form.List>

                <Divider orientation="left">构建平台（可多选）</Divider>
                <Form.Item name="targetKeys" rules={[{ required: true, message: '至少选择一个构建平台' }]}>
                    <Checkbox.Group options={TARGETS.map(item => ({ label: item.name, value: item.key }))} />
                </Form.Item>
                {selectedTargets.map(key => {
                    const target = TARGETS.find(item => item.key === key)
                    const options = target.platform === 'windows'
                        ? (templateKey === 'tauri-desktop' ? ['windows-vm'] : ['windows-cross'])
                        : [target.executor]
                    return <Row key={key} gutter={16} align="middle"><Col span={8}><Text>{target.name}</Text></Col><Col span={10}><Form.Item name={['executors', key]} label="执行器" initialValue={executorForTarget(target, templateKey)} rules={[{ required: true }]}><Select>{options.map(value => <Option key={value} value={value}>{value}</Option>)}</Select></Form.Item></Col></Row>
                })}
                <Alert style={{ marginTop: 12 }} type="warning" showIcon message="选择平台表示该项目支持此构建目标；例如 Toolkit 当前不需要 Linux，就不要勾选 Linux。" />
            </Form>
        </Drawer>
    </>
}
